import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowUpRight,
  ArrowRight,
  Check,
  Minus,
  Plus,
  Quote,
  Sparkles,
  Search,
  Bot,
  LineChart,
  BookOpen,
  Layers,
  Compass,
  Mail,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------- Utilities -------------------- */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

/* -------------------- Nav -------------------- */
const Nav = () => {
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#about", label: "About" },
    { href: "#curriculum", label: "Curriculum" },
    { href: "#voices", label: "Voices" },
    { href: "#pricing", label: "Pricing" },
    { href: "#faq", label: "FAQ" },
  ];
  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-paper/80 border-b border-paper-rule"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="w-8 h-8 border border-ink flex items-center justify-center font-serif text-base group-hover:bg-ink group-hover:text-paper transition-colors">
            P
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-lg tracking-tight">Predrag Petrovic</span>
            <span className="eyebrow text-[10px]">AI SEO Training</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-9 text-sm text-ink-muted">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="hover:text-ink transition-colors relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-px after:bg-ink hover:after:w-full after:transition-all after:duration-500"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          data-testid="nav-cta-book-call"
          className="hidden md:inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 text-sm uppercase tracking-[0.18em] hover:bg-paper hover:text-ink hover:border-ink border border-ink transition-colors"
        >
          Book a Call <ArrowUpRight size={14} />
        </a>
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden border border-ink px-3 py-2 text-xs uppercase tracking-[0.18em]"
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-paper-rule bg-paper">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="text-base text-ink-soft"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="mt-2 inline-flex items-center justify-between bg-ink text-paper px-5 py-3 text-sm uppercase tracking-[0.18em]"
            >
              Book a Call <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* -------------------- Hero -------------------- */
const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative pt-28 md:pt-36 pb-16 md:pb-24 border-b border-paper-rule overflow-hidden"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 lg:col-span-8">
          <div className="flex items-center gap-4 mb-8 reveal">
            <span className="eyebrow">— A Course by Predrag Petrovic</span>
            <span className="hidden md:inline-block w-16 h-px bg-ink-faint" />
            <span className="hidden md:inline eyebrow text-ink-faint">
              Cohort 04 · 2026
            </span>
          </div>

          <h1
            className="serif-display text-[44px] sm:text-6xl lg:text-[88px] xl:text-[104px] reveal"
            data-testid="hero-headline"
          >
            The search results page <br />
            is being <em className="italic-accent text-ink-muted">rewritten.</em>
            <br />
            Make sure your brand <br />
            is what gets <span className="italic-accent">cited.</span>
          </h1>

          <div className="mt-10 md:mt-14 max-w-xl reveal">
            <p className="text-base md:text-lg text-ink-muted leading-relaxed">
              For eighteen years I&apos;ve helped brands win the search results page.
              Today the search results page is being rewritten — by large
              language models, by answer engines, by an audience that increasingly
              never clicks. My work is to make sure the brands I partner with
              don&apos;t just survive that shift, but compound through it.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-between gap-6 bg-ink text-paper px-7 py-4 text-sm uppercase tracking-[0.2em] hover:bg-paper hover:text-ink border border-ink transition-colors"
              >
                Book a Discovery Call
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>
              <a
                href="#curriculum"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-between gap-6 px-7 py-4 text-sm uppercase tracking-[0.2em] border border-ink text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                See the Curriculum <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* Right column — editorial info card */}
        <aside className="hidden lg:flex col-span-12 lg:col-span-4 flex-col justify-between border-l border-paper-rule pl-8">
          <div>
            <div className="eyebrow mb-4">Index № 01</div>
            <div className="flex flex-col gap-5 text-sm text-ink-muted">
              <Row k="Format" v="6 weeks · live cohort" />
              <Row k="Sessions" v="12 · 2 per week" />
              <Row k="Group" v="Capped at 24 founders" />
              <Row k="Includes" v="1:1 audit · workbook" />
              <Row k="Next start" v="February 2026" />
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-paper-rule">
            <div className="serif-display text-7xl leading-none">18</div>
            <p className="mt-2 eyebrow">Years winning the SERP</p>
          </div>
        </aside>
      </div>

      {/* Trust strip */}
      <div className="mt-16 md:mt-24 pt-8 border-t border-paper-rule reveal">
        <div className="flex items-center justify-between flex-wrap gap-6">
          <span className="eyebrow">Trusted by operators at</span>
          <div className="flex items-center gap-x-10 gap-y-3 flex-wrap font-serif text-xl md:text-2xl text-ink-muted">
            {["Lumen & Co.", "Norden Studio", "Avery Health", "Kiln Press", "Maison Rives", "Otto / Vault"].map((n) => (
              <span
                key={n}
                className="hover:text-ink transition-colors"
                data-testid={`trust-logo-${n.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              >
                {n}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Row = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-6 border-b border-paper-rule pb-3">
    <span className="eyebrow text-ink-faint">{k}</span>
    <span className="text-ink-soft text-right">{v}</span>
  </div>
);

/* -------------------- About -------------------- */
const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="border-b border-paper-rule"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-32">
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-3">
          <div className="eyebrow">About — § 02</div>
          <h2 className="serif-display text-3xl md:text-4xl mt-4 reveal">
            A practitioner, <br />
            <em className="italic-accent text-ink-muted">not a pundit.</em>
          </h2>
        </div>

        <div className="col-span-12 md:col-span-5 reveal">
          <p className="text-lg text-ink-soft leading-relaxed">
            I started in 2007, the year Google launched Universal Search. I&apos;ve
            shipped technical migrations for newsrooms, written content systems
            for D2C brands, and rebuilt internal linking graphs for SaaS
            companies preparing for IPO.
          </p>
          <p className="text-lg text-ink-soft leading-relaxed mt-6">
            The principles haven&apos;t changed: clarity, authority, distribution.
            The surface has — and that&apos;s the entire point of this training.
            ChatGPT, Perplexity, Google AI Overviews and the answer-engine
            economy demand a new craft. <em className="italic-accent">I teach that craft.</em>
          </p>

          <div className="mt-10 grid grid-cols-3 border-t border-paper-rule">
            {[
              { n: "230+", l: "Brands advised" },
              { n: "$1.4B", l: "Pipeline influenced" },
              { n: "62", l: "Countries shipped in" },
            ].map((s) => (
              <div
                key={s.l}
                className="border-r last:border-r-0 border-paper-rule py-6"
              >
                <div className="serif-display text-3xl md:text-4xl">{s.n}</div>
                <div className="eyebrow mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Portrait bento */}
        <div className="col-span-12 md:col-span-4 reveal">
          <div className="relative border border-paper-rule overflow-hidden h-[440px] md:h-[520px]">
            <img
              src="https://images.unsplash.com/photo-1629507208649-70919ca33793"
              alt="Predrag Petrovic, AI SEO trainer"
              className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
              data-testid="about-portrait"
            />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-paper border-t border-paper-rule flex items-center justify-between">
              <div>
                <div className="font-serif text-lg leading-none">
                  Predrag Petrovic
                </div>
                <div className="eyebrow mt-1.5">Belgrade · Remote-first</div>
              </div>
              <Sparkles size={18} className="text-ink-muted" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------- Curriculum -------------------- */
const modules = [
  {
    n: "01",
    title: "Foundations of the AI Search Era",
    desc: "Why the click is dying, what replaces it, and how to read SERP volatility through the lens of LLMs and answer engines.",
    icon: Compass,
    tag: "Week 01",
  },
  {
    n: "02",
    title: "Crawl, Render, Retrieve",
    desc: "Technical SEO refit for retrieval-augmented systems: site architecture, structured data, and machine-readable authority signals.",
    icon: Layers,
    tag: "Week 02",
  },
  {
    n: "03",
    title: "Content Engineered to be Cited",
    desc: "Designing pages that LLMs lift verbatim. Editorial structure, evidence density, and the art of the quotable paragraph.",
    icon: BookOpen,
    tag: "Week 03",
  },
  {
    n: "04",
    title: "Authority in the Age of Answers",
    desc: "Earning placements in ChatGPT, Perplexity, Claude and Google AI Overviews. Outreach, digital PR and entity building.",
    icon: Bot,
    tag: "Week 04",
  },
  {
    n: "05",
    title: "Measurement Without the Click",
    desc: "Building a dashboard for the post-click world: brand impressions, mention share, citation velocity, assisted revenue.",
    icon: LineChart,
    tag: "Week 05",
  },
  {
    n: "06",
    title: "Compounding Strategy & Audit",
    desc: "Live 1:1 audit of your domain. We finish with a 12-month roadmap that survives the next algorithmic rewrite.",
    icon: Search,
    tag: "Week 06",
  },
];

const Curriculum = () => (
  <section
    id="curriculum"
    data-testid="curriculum-section"
    className="border-b border-paper-rule bg-paper-tint"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-32">
      <div className="grid grid-cols-12 gap-10 mb-14 md:mb-20">
        <div className="col-span-12 md:col-span-5">
          <div className="eyebrow">Curriculum — § 03</div>
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4 reveal">
            Six weeks. <br />
            <em className="italic-accent">A complete rewrite</em> <br />
            of how you do search.
          </h2>
        </div>
        <div className="col-span-12 md:col-span-6 md:col-start-7 self-end reveal">
          <p className="text-lg text-ink-muted leading-relaxed">
            Each module ships you a working artefact — a technical brief, a
            content system, a measurement dashboard — that your team uses the
            following Monday. We do not deal in theory.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-paper-rule">
        {modules.map((m) => {
          const Icon = m.icon;
          return (
            <article
              key={m.n}
              data-testid={`module-${m.n}`}
              className="group relative border-r border-b border-paper-rule p-8 md:p-10 hover:bg-paper transition-colors duration-500 reveal"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="serif-display text-7xl md:text-8xl text-ink-faint group-hover:text-ink transition-colors duration-700">
                  {m.n}
                </div>
                <div className="flex items-center gap-3">
                  <span className="eyebrow text-ink-faint">{m.tag}</span>
                  <Icon size={18} className="text-ink-muted" />
                </div>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight">
                {m.title}
              </h3>
              <p className="mt-4 text-ink-muted text-base md:text-[17px] leading-relaxed max-w-md">
                {m.desc}
              </p>
              <div className="mt-8 flex items-center gap-2 eyebrow group-hover:text-ink transition-colors">
                Read module brief
                <ArrowUpRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </div>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* -------------------- Voices / Testimonials -------------------- */
const voices = [
  {
    quote:
      "Predrag rebuilt our content engine the week ChatGPT shipped browse mode. We didn't lose a quarter — we gained one. He sees around corners.",
    name: "Marielle Sanderson",
    role: "VP Marketing, Avery Health",
  },
  {
    quote:
      "Most SEOs sell traffic. Predrag sells authority. He understands that being cited by an LLM is the new front page.",
    name: "Jonas Aalto",
    role: "Founder, Norden Studio",
  },
  {
    quote:
      "The training is unglamorous in the best way. Six weeks of small, sharp decisions that compound. Our share of voice in AI answers tripled.",
    name: "Priya Chand",
    role: "Head of Growth, Kiln Press",
  },
];

const Voices = () => (
  <section
    id="voices"
    data-testid="voices-section"
    className="border-b border-paper-rule"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-32">
      <div className="flex items-end justify-between mb-14 md:mb-20 flex-wrap gap-6">
        <div>
          <div className="eyebrow">Voices — § 04</div>
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4 max-w-2xl reveal">
            What operators say <br />
            <em className="italic-accent">after the cohort.</em>
          </h2>
        </div>
        <Quote className="text-ink-faint" size={56} strokeWidth={1} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
        {voices.map((v, i) => (
          <figure
            key={i}
            data-testid={`testimonial-${i + 1}`}
            className="reveal"
          >
            <blockquote className="serif-display text-2xl md:text-[28px] leading-[1.25] text-ink-soft">
              <span className="text-ink-faint">“</span>
              {v.quote}
              <span className="text-ink-faint">”</span>
            </blockquote>
            <figcaption className="mt-8 pt-6 border-t border-paper-rule">
              <div className="font-serif text-lg">{v.name}</div>
              <div className="eyebrow mt-1.5">{v.role}</div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------- Pricing -------------------- */
const Pricing = () => (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="border-b border-paper-rule bg-paper-tint"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-32">
      <div className="grid grid-cols-12 gap-10 mb-14 md:mb-20">
        <div className="col-span-12 md:col-span-6">
          <div className="eyebrow">Investment — § 05</div>
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4 reveal">
            Two ways to <br />
            <em className="italic-accent">work together.</em>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-5 md:col-start-8 self-end reveal">
          <p className="text-ink-muted leading-relaxed">
            Both options include the workbook, the live recordings, and a
            personal audit. Cohorts run twice a year and tend to fill from a
            waitlist.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-l border-paper-rule">
        {/* Plan A */}
        <div
          data-testid="pricing-plan-cohort"
          className="border-r border-b border-paper-rule p-10 md:p-14 bg-paper"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="eyebrow">Plan A · Cohort</div>
            <div className="eyebrow text-ink-faint">24 seats</div>
          </div>
          <h3 className="font-serif text-3xl md:text-4xl">Live Group Training</h3>
          <p className="mt-3 text-ink-muted">
            The complete six-week curriculum, taught live by Predrag. Built for
            in-house teams and senior operators.
          </p>
          <div className="mt-8 flex items-baseline gap-4">
            <span className="serif-display text-6xl md:text-7xl">€2,400</span>
            <span className="line-through text-ink-faint">€3,200</span>
            <span className="eyebrow text-ink-muted">per seat</span>
          </div>
          <ul className="mt-10 space-y-3">
            {[
              "12 live sessions across 6 weeks",
              "Private Slack with daily review",
              "60-minute 1:1 audit with Predrag",
              "All workbooks, templates, prompts",
              "Lifetime access to recordings",
            ].map((f) => (
              <li key={f} className="flex items-start gap-3 text-ink-soft">
                <Check size={16} className="mt-1 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <a
            href="#contact"
            data-testid="pricing-cta-cohort"
            className="mt-10 group inline-flex items-center justify-between w-full bg-ink text-paper px-7 py-4 text-sm uppercase tracking-[0.2em] hover:bg-paper hover:text-ink border border-ink transition-colors"
          >
            Reserve a Seat
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
        </div>

        {/* Plan B */}
        <div
          data-testid="pricing-plan-private"
          className="border-r border-b border-paper-rule p-10 md:p-14 bg-paper relative"
        >
          <div className="absolute top-0 right-0 bg-ink text-paper px-4 py-2 eyebrow">
            Most personal
          </div>
          <div className="flex items-center justify-between mb-6">
            <div className="eyebrow">Plan B · 1:1 Consulting</div>
            <div className="eyebrow text-ink-faint">3 clients / quarter</div>
          </div>
          <h3 className="font-serif text-3xl md:text-4xl">
            Private Engagement
          </h3>
          <p className="mt-3 text-ink-muted">
            A bespoke ninety-day program for founders and CMOs who want
            Predrag in the room. Strategy, hands-on execution, and team
            enablement.
          </p>
          <div className="mt-8 flex items-baseline gap-4">
            <span className="serif-display text-6xl md:text-7xl">€18,000</span>
            <span className="eyebrow text-ink-muted">per quarter</span>
          </div>
          <ul className="mt-10 space-y-3">
            {[
              "Weekly 90-min strategy sessions",
              "Full technical and content audit",
              "Custom AI-search measurement system",
              "Priority Slack & async reviews",
              "Team workshop included",
            ].map((f) => (
              <li key={f} className="flex items-start gap-3 text-ink-soft">
                <Check size={16} className="mt-1 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <a
            href="#contact"
            data-testid="pricing-cta-private"
            className="mt-10 group inline-flex items-center justify-between w-full bg-paper text-ink border border-ink px-7 py-4 text-sm uppercase tracking-[0.2em] hover:bg-ink hover:text-paper transition-colors"
          >
            Apply for a Slot
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------- FAQ -------------------- */
const faqs = [
  {
    q: "Who is this training built for?",
    a: "Senior in-house marketers, founders, and consultants who already understand classic SEO and need a credible playbook for the AI-search era. If you have never launched a content program before, this isn't the right starting point.",
  },
  {
    q: "Will the techniques still work in twelve months?",
    a: "We don't teach tactics that depend on a specific Google update. We teach principles — entity authority, citation worthiness, retrieval-friendly architecture — that compound across model changes. The roadmap I leave you with is built to survive at least three algorithmic rewrites.",
  },
  {
    q: "How is this different from a generic 'AI for SEO' course?",
    a: "Most courses teach you to write blog posts faster with ChatGPT. This course teaches you how your brand becomes the source ChatGPT cites. The difference is everything.",
  },
  {
    q: "Do I need a large existing site?",
    a: "No. The framework works for early-stage brands and incumbents. We adapt the audit and the roadmap to your stage during the 1:1.",
  },
  {
    q: "What's the time commitment?",
    a: "Plan for roughly six hours per week — two live sessions, plus async work and the implementation of each module's artefact.",
  },
  {
    q: "Is there a payment plan?",
    a: "Yes. The cohort can be split across two installments. The private engagement is billed monthly. Mention it on the call.",
  },
];

const FAQ = () => (
  <section
    id="faq"
    data-testid="faq-section"
    className="border-b border-paper-rule"
  >
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-32">
      <div className="mb-14 md:mb-20">
        <div className="eyebrow">Questions — § 06</div>
        <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4 reveal">
          Frequently asked, <br />
          <em className="italic-accent">honestly answered.</em>
        </h2>
      </div>

      <Accordion type="single" collapsible className="w-full">
        {faqs.map((f, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            data-testid={`faq-item-${i + 1}`}
            className="border-b border-paper-rule first:border-t"
          >
            <AccordionTrigger
              data-testid={`faq-trigger-${i + 1}`}
              className="py-7 text-left font-serif text-xl md:text-2xl tracking-tight hover:no-underline group"
            >
              <span className="flex items-center gap-6">
                <span className="eyebrow text-ink-faint w-8">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="group-hover:italic transition-all">{f.q}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-7 pl-14 text-ink-muted text-base md:text-lg leading-relaxed">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* -------------------- Contact -------------------- */
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        intent: "discovery_call",
      });
      toast.success("Sent. Predrag will personally reply within 48 hours.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please email predrag directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-b border-paper-rule bg-ink text-paper"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="eyebrow text-paper/60">Contact — § 07</div>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4">
              Book a <em className="italic-accent text-paper/70">discovery</em> <br />
              call.
            </h2>
            <p className="mt-8 text-paper/70 leading-relaxed text-lg max-w-md">
              Tell me about your brand and where you want to be cited. Thirty
              minutes, no slides, no pitch deck — just a frank conversation
              about what would actually move the needle.
            </p>

            <div className="mt-12 space-y-5">
              <div className="flex items-center gap-3 text-paper/80">
                <Mail size={16} />
                <a
                  href="mailto:hello@predragpetrovic.co"
                  data-testid="contact-email"
                  className="hover:text-paper border-b border-paper/30 hover:border-paper transition-colors"
                >
                  hello@predragpetrovic.co
                </a>
              </div>
              <div className="grid grid-cols-2 gap-6 pt-8 border-t border-paper/15 max-w-md">
                <div>
                  <div className="eyebrow text-paper/50">Based in</div>
                  <div className="font-serif text-xl mt-1">Belgrade, RS</div>
                </div>
                <div>
                  <div className="eyebrow text-paper/50">Working with</div>
                  <div className="font-serif text-xl mt-1">EU · US · UK</div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="col-span-12 lg:col-span-6 lg:col-start-7"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Name" name="name" value={form.name} onChange={handleChange} testid="contact-input-name" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} testid="contact-input-email" />
              <div className="md:col-span-2">
                <Field label="Company" name="company" value={form.company} onChange={handleChange} testid="contact-input-company" required={false} />
              </div>
              <div className="md:col-span-2">
                <Label
                  htmlFor="message"
                  className="eyebrow text-paper/60 mb-3 block"
                >
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="What do you want to compound through the AI-search shift?"
                  className="bg-transparent border-0 border-b border-paper/30 rounded-none focus-visible:ring-0 focus-visible:border-paper px-0 py-3 text-paper placeholder:text-paper/30 text-base resize-none"
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="text-paper/50 text-sm max-w-xs">
                Replies within 48 hours. No automation, no funnel — Predrag
                writes back personally.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center justify-between gap-8 bg-paper text-ink px-7 py-4 text-sm uppercase tracking-[0.2em] hover:bg-transparent hover:text-paper border border-paper transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Request a Call"}
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", value, onChange, testid, required = true }) => (
  <div>
    <Label htmlFor={name} className="eyebrow text-paper/60 mb-3 block">
      {label}
    </Label>
    <Input
      id={name}
      name={name}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="bg-transparent border-0 border-b border-paper/30 rounded-none focus-visible:ring-0 focus-visible:border-paper px-0 py-3 text-paper placeholder:text-paper/30 text-base h-auto"
    />
  </div>
);

/* -------------------- Footer -------------------- */
const Footer = () => (
  <footer data-testid="site-footer" className="bg-paper">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
        <div>
          <div className="serif-display text-5xl md:text-7xl leading-none">
            Predrag <em className="italic-accent text-ink-muted">Petrovic.</em>
          </div>
          <div className="eyebrow mt-4">Independent practice · Est. 2007</div>
        </div>
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 text-sm text-ink-muted">
          <a href="#about" className="hover:text-ink">About</a>
          <a href="#curriculum" className="hover:text-ink">Curriculum</a>
          <a href="#pricing" className="hover:text-ink">Pricing</a>
          <a href="#contact" className="hover:text-ink">Book a Call</a>
        </div>
      </div>
      <div className="mt-12 pt-8 border-t border-paper-rule flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-ink-faint uppercase tracking-[0.18em]">
        <div>© {new Date().getFullYear()} Predrag Petrovic — All rights reserved.</div>
        <div>Crafted in Belgrade · Set in Cormorant & Outfit</div>
      </div>
    </div>
  </footer>
);

/* -------------------- Page -------------------- */
const Landing = () => {
  useReveal();
  // ping API on mount (non-blocking)
  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#FBFBF9",
            border: "1px solid #0A0A0A",
            borderRadius: 0,
            fontFamily: "Outfit, sans-serif",
          },
        }}
      />
      <Nav />
      <main>
        <Hero />
        <About />
        <Curriculum />
        <Voices />
        <Pricing />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
